/********************************************************************************
 * Copyright (c) 2019 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { createAbsoluteURL, addQuery } from './utils';
import { sendRequest } from './server-request';
export class ExtensionRegistryService {
    constructor(serverUrl = '', AdminConstructor = AdminServiceImpl) {
        this.serverUrl = serverUrl;
        this.admin = new AdminConstructor(this);
    }
    async getLoginProviders(signal) {
        const endpoint = createAbsoluteURL([this.serverUrl, 'login-providers']);
        return sendRequest({ signal, endpoint });
    }
    getLogoutUrl() {
        return createAbsoluteURL([this.serverUrl, 'logout']);
    }
    getExtensionApiUrl(ext) {
        const arr = [this.serverUrl, 'api', ext.namespace, ext.name];
        if (ext.target) {
            arr.push(ext.target);
        }
        if (ext.version) {
            arr.push(ext.version);
        }
        return createAbsoluteURL(arr);
    }
    /**
     * Fetches the download time series for an extension from the analytics endpoint, which responds
     * 404 where download analytics is not configured. Callers showing it should gate on
     * {@link SiteSettings.downloadAnalyticsVisible}. The server fixes the window: the daily
     * downloads of the last 52 weeks, ending today.
     */
    async getExtensionDownloadSeries(signal, params) {
        const endpoint = createAbsoluteURL([
            this.serverUrl,
            'api',
            params.namespace,
            params.name,
            'analytics',
            'downloads'
        ]);
        return sendRequest({ signal, endpoint });
    }
    /** The public namespace listing, which names only the namespace's active extensions. */
    async getPublicNamespace(signal, name) {
        return sendRequest({ signal, endpoint: createAbsoluteURL([this.serverUrl, 'api', name]) });
    }
    async getNamespaceDetails(signal, name) {
        const endpoint = createAbsoluteURL([this.serverUrl, 'api', name, 'details']);
        return sendRequest({ signal, endpoint });
    }
    async setNamespaceDetails(endpoint, details) {
        const csrfToken = await this.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            payload: details,
            credentials: true,
            endpoint,
            headers
        });
    }
    async setNamespaceLogo(endpoint, logoFile, logoName) {
        const csrfToken = await this.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        const form = new FormData();
        form.append('file', logoFile, logoName);
        endpoint = createAbsoluteURL([endpoint, 'logo']);
        return sendRequest({
            method: 'POST',
            payload: form,
            credentials: true,
            endpoint,
            headers
        });
    }
    async search(signal, filter) {
        const query = [];
        if (filter) {
            if (filter.query)
                query.push({ key: 'query', value: filter.query });
            if (filter.category)
                query.push({ key: 'category', value: filter.category });
            if (filter.offset)
                query.push({ key: 'offset', value: filter.offset });
            if (filter.size)
                query.push({ key: 'size', value: filter.size });
            if (filter.sortBy)
                query.push({ key: 'sortBy', value: filter.sortBy });
            if (filter.sortOrder)
                query.push({ key: 'sortOrder', value: filter.sortOrder });
        }
        const endpoint = createAbsoluteURL([this.serverUrl, 'api', '-', 'search'], query);
        return sendRequest({ signal, endpoint });
    }
    async getExtensionDetail(signal, extensionUrl) {
        return sendRequest({ signal, endpoint: extensionUrl });
    }
    async getExtensionReadme(signal, extension) {
        return sendRequest({
            signal,
            endpoint: extension.files.readme,
            headers: { Accept: 'text/plain' },
            followRedirect: true
        });
    }
    async getExtensionChangelog(signal, extension) {
        return sendRequest({
            signal,
            endpoint: extension.files.changelog,
            headers: { Accept: 'text/plain' },
            followRedirect: true
        });
    }
    async getExtensionIcon(signal, extension) {
        if (!extension.files?.icon) {
            return Promise.resolve(undefined);
        }
        return sendRequest({
            signal,
            endpoint: extension.files.icon,
            headers: { Accept: 'application/octet-stream' },
            followRedirect: true
        }).then(value => {
            const blob = value;
            return URL.createObjectURL(blob);
        });
    }
    async getExtensionReviews(signal, extension) {
        return sendRequest({ signal, endpoint: extension.reviewsUrl });
    }
    async postReview(review, postReviewUrl) {
        const csrfToken = await this.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            payload: review,
            credentials: true,
            endpoint: postReviewUrl,
            headers
        });
    }
    async deleteReview(deleteReviewUrl) {
        const csrfToken = await this.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: deleteReviewUrl,
            headers
        });
    }
    async deleteUserReview(extension, user) {
        const csrfToken = await this.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([
                this.serverUrl,
                'admin',
                'extension',
                extension.namespace,
                extension.name,
                'review',
                user.provider || 'github',
                user.loginName,
                'delete'
            ]),
            headers
        });
    }
    /** Rejects with a 200 error result when nobody is logged in. */
    async getUser(signal) {
        return sendRequest({
            signal,
            endpoint: createAbsoluteURL([this.serverUrl, 'user']),
            credentials: true
        });
    }
    /** Rejects with the error the last login failed with, which the registry keeps in the session. */
    async getUserAuthError(signal) {
        const loginError = await sendRequest({
            signal,
            endpoint: createAbsoluteURL([this.serverUrl, 'user', 'auth-error']),
            credentials: true
        });
        // The registry answers 200 with no `error` field; the status keeps the query client from retrying it.
        throw { ...loginError, status: 200 };
    }
    async getUserByName(signal, name) {
        return sendRequest({
            signal,
            endpoint: createAbsoluteURL([this.serverUrl, 'user', 'search', name]),
            credentials: true
        });
    }
    async getAccessTokens(signal, user) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: user.tokensUrl
        });
    }
    async createAccessToken(user, description, scope) {
        const csrfToken = await this.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        const endpoint = addQuery(user.createTokenUrl, [
            { key: 'description', value: description },
            { key: 'namespace', value: scope?.namespace },
            { key: 'extension', value: scope?.extension },
            { key: 'publishingOnly', value: scope?.publishingOnly ? 'true' : undefined }
        ]);
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint,
            headers
        });
    }
    async deleteAccessToken(token) {
        const csrfToken = await this.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: token.deleteTokenUrl,
            headers
        });
    }
    async deleteAllAccessTokens(tokens) {
        const csrfToken = await this.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return await Promise.all(tokens.map(token => sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: token.deleteTokenUrl,
            headers
        })));
    }
    async getCsrfToken(signal) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.serverUrl, 'user', 'csrf'])
        });
    }
    async getNamespaces(signal) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.serverUrl, 'user', 'namespaces'])
        });
    }
    async getNamespaceMembers(signal, namespace) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: namespace.membersUrl
        });
    }
    async setNamespaceMember(endpoint, user, role) {
        const csrfToken = await this.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        const query = [
            { key: 'user', value: user.loginName },
            { key: 'provider', value: user.provider },
            { key: 'role', value: role }
        ];
        return sendRequest({
            headers,
            method: 'POST',
            credentials: true,
            endpoint: addQuery(endpoint, query)
        });
    }
    // The trusted-publishing base URL for a namespace in the user context. Callers that hold a
    // Namespace use its server-provided `trustedPublishingUrl`; the extension page only has a name.
    userTrustedPublishingUrl(namespace) {
        return createAbsoluteURL([this.serverUrl, 'user', 'namespace', namespace, 'trusted-publishing']);
    }
    // Requires a logged-in user; the server answers 401 otherwise.
    async getTrustedPublishingStatus(signal) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.serverUrl, 'api', '-', 'trusted-publishing', 'status'])
        });
    }
    async getTrustedPublishers(signal, trustedPublishingUrl) {
        const result = await sendRequest({
            signal,
            credentials: true,
            endpoint: trustedPublishingUrl
        });
        return {
            trustedPublishers: result.trustedPublishers ?? [],
            registrableExtensions: result.registrableExtensions ?? []
        };
    }
    async registerTrustedPublisher(trustedPublishingUrl, request) {
        const csrfToken = await this.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            payload: request,
            headers,
            endpoint: createAbsoluteURL([trustedPublishingUrl, 'create'])
        });
    }
    async deleteTrustedPublisher(trustedPublishingUrl, id) {
        const csrfToken = await this.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            headers,
            endpoint: createAbsoluteURL([trustedPublishingUrl, 'delete', `${id}`])
        });
    }
    async signPublisherAgreement() {
        const csrfToken = await this.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([this.serverUrl, 'user', 'publisher-agreement']),
            headers
        });
    }
    async getStaticContent(signal, url) {
        return sendRequest({
            signal,
            endpoint: url,
            headers: { Accept: 'text/plain' },
            followRedirect: true
        });
    }
    async publishExtension(extensionPackage) {
        const csrfToken = await this.getCsrfToken();
        const headers = {
            'Content-Type': 'application/octet-stream'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            payload: extensionPackage,
            headers: headers,
            endpoint: createAbsoluteURL([this.serverUrl, 'api', 'user', 'publish'])
        });
    }
    async createNamespace(name) {
        const csrfToken = await this.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            payload: { name: name },
            headers: headers,
            endpoint: createAbsoluteURL([this.serverUrl, 'api', 'user', 'namespace', 'create'])
        });
    }
    async getExtensions(signal) {
        const headers = {};
        return sendRequest({
            signal,
            method: 'GET',
            credentials: true,
            headers: headers,
            endpoint: createAbsoluteURL([this.serverUrl, 'user', 'extensions'])
        });
    }
    async getExtension(signal, namespace, extension) {
        const headers = {};
        return sendRequest({
            signal,
            method: 'GET',
            credentials: true,
            headers: headers,
            endpoint: createAbsoluteURL([this.serverUrl, 'user', 'extension', namespace, extension])
        });
    }
    async deleteExtensions(req) {
        const csrfToken = await this.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([this.serverUrl, 'user', 'extension', req.namespace, req.extension, 'delete']),
            headers,
            payload: req.targetPlatformVersions
        });
    }
    /**
     * `revalidate` forces the response to be re-checked with the registry. It is served with
     * `max-age=300`, so a caller about to act on a value that an admin may have just changed - the
     * size ceiling - would otherwise be handed the same cached copy it is trying to get past.
     */
    async getRegistryVersion(signal, options = {}) {
        const endpoint = createAbsoluteURL([this.serverUrl, 'api', 'version']);
        const headers = options.revalidate ? { 'Cache-Control': 'no-cache' } : undefined;
        return sendRequest({ signal, endpoint, headers });
    }
    async getSiteSettings(signal) {
        const endpoint = createAbsoluteURL([this.serverUrl, 'api', '-', 'settings']);
        return sendRequest({ signal, endpoint });
    }
}
export class AdminServiceImpl {
    constructor(registry) {
        this.registry = registry;
    }
    async getExtension(signal, namespace, extension) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'extension', namespace, extension])
        });
    }
    async deleteExtensions(req) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([
                this.registry.serverUrl,
                'admin',
                'extension',
                req.namespace,
                req.extension,
                'delete'
            ]),
            headers,
            payload: req.targetPlatformVersions
        });
    }
    async purgeExtensions(req) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([
                this.registry.serverUrl,
                'admin',
                'extension',
                req.namespace,
                req.extension,
                'purge'
            ]),
            headers,
            payload: req.targetPlatformVersions
        });
    }
    async getNamespace(signal, name) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'namespace', name])
        });
    }
    async createNamespace(namespace) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'create-namespace']),
            method: 'POST',
            payload: namespace,
            headers
        });
    }
    async deleteNamespace(namespace) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'namespace', namespace.name]),
            method: 'DELETE',
            headers
        });
    }
    async changeNamespace(req) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'change-namespace']),
            method: 'POST',
            payload: req,
            headers
        });
    }
    async getPublisherInfo(signal, provider, login) {
        return sendRequest({
            signal,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'publisher', provider, login]),
            credentials: true
        });
    }
    async getUsers(signal, params) {
        const query = [];
        if (params) {
            if (params.search) {
                query.push({ key: 'query', value: params.search });
            }
            if (params.role) {
                query.push({ key: 'role', value: params.role });
            }
            if (params.page !== undefined) {
                query.push({ key: 'page', value: params.page });
            }
            if (params.size !== undefined) {
                query.push({ key: 'size', value: params.size });
            }
        }
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'user', 'search'], query)
        });
    }
    async getPermissions(signal) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'api', 'permissions'])
        });
    }
    async updateUserAccess(provider, login, access) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'PUT',
            payload: access,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'user', provider, login, 'access']),
            headers
        });
    }
    async revokePublisherContributions(provider, login) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'publisher', provider, login, 'revoke']),
            headers
        });
    }
    async reinstatePublisherAgreement(provider, login) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([
                this.registry.serverUrl,
                'admin',
                'publisher',
                provider,
                login,
                'reinstate-agreement'
            ]),
            headers
        });
    }
    async revokeAccessTokens(provider, login) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([
                this.registry.serverUrl,
                'admin',
                'publisher',
                provider,
                login,
                'tokens',
                'revoke'
            ]),
            headers
        });
    }
    async forgetUser(provider, login) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'publisher', provider, login, 'delete']),
            headers
        });
    }
    async getAllScans(signal, params) {
        const query = [];
        if (params) {
            if (params.size !== undefined)
                query.push({ key: 'size', value: params.size });
            if (params.offset !== undefined)
                query.push({ key: 'offset', value: params.offset });
            if (params.status) {
                const statusValue = Array.isArray(params.status) ? params.status.join(',') : params.status;
                query.push({ key: 'status', value: statusValue });
            }
            if (params.publisher)
                query.push({ key: 'publisher', value: params.publisher });
            if (params.namespace)
                query.push({ key: 'namespace', value: params.namespace });
            if (params.name)
                query.push({ key: 'name', value: params.name });
            if (params.validationType && params.validationType.length > 0)
                query.push({ key: 'validationType', value: params.validationType.join(',') });
            if (params.threatScannerName && params.threatScannerName.length > 0)
                query.push({ key: 'threatScannerName', value: params.threatScannerName.join(',') });
            if (params.dateStartedFrom)
                query.push({ key: 'dateStartedFrom', value: params.dateStartedFrom });
            if (params.dateStartedTo)
                query.push({ key: 'dateStartedTo', value: params.dateStartedTo });
            if (params.enforcement)
                query.push({ key: 'enforcement', value: params.enforcement });
            if (params.adminDecision && params.adminDecision.length > 0)
                query.push({ key: 'adminDecision', value: params.adminDecision.join(',') });
            if (params.exactStatus)
                query.push({ key: 'exactStatus', value: 'true' });
        }
        const endpoint = createAbsoluteURL([this.registry.serverUrl, 'admin', 'scans'], query);
        return sendRequest({
            signal,
            credentials: true,
            endpoint
        });
    }
    async getScan(signal, scanId) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'scans', scanId])
        });
    }
    async retryFailedScannerJobs(scanId) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {};
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'scans', scanId, 'jobs', 'retry']),
            headers
        });
    }
    async getScanCounts(signal, params) {
        const query = [];
        if (params) {
            if (params.dateStartedFrom)
                query.push({ key: 'dateStartedFrom', value: params.dateStartedFrom });
            if (params.dateStartedTo)
                query.push({ key: 'dateStartedTo', value: params.dateStartedTo });
            if (params.enforcement)
                query.push({ key: 'enforcement', value: params.enforcement });
            if (params.threatScannerName && params.threatScannerName.length > 0) {
                query.push({ key: 'threatScannerName', value: params.threatScannerName.join(',') });
            }
            if (params.validationType && params.validationType.length > 0) {
                query.push({ key: 'validationType', value: params.validationType.join(',') });
            }
        }
        const endpoint = createAbsoluteURL([this.registry.serverUrl, 'admin', 'scans', 'counts'], query);
        return sendRequest({
            signal,
            credentials: true,
            endpoint
        });
    }
    async getScanFilterOptions(signal) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'scans', 'filterOptions'])
        });
    }
    async getFiles(signal, params) {
        const query = [];
        if (params) {
            if (params.size !== undefined)
                query.push({ key: 'size', value: params.size });
            if (params.offset !== undefined)
                query.push({ key: 'offset', value: params.offset });
            if (params.decision)
                query.push({ key: 'decision', value: params.decision });
            if (params.publisher)
                query.push({ key: 'publisher', value: params.publisher });
            if (params.namespace)
                query.push({ key: 'namespace', value: params.namespace });
            if (params.name)
                query.push({ key: 'name', value: params.name });
            if (params.dateDecidedFrom)
                query.push({ key: 'dateDecidedFrom', value: params.dateDecidedFrom });
            if (params.dateDecidedTo)
                query.push({ key: 'dateDecidedTo', value: params.dateDecidedTo });
            if (params.sortBy)
                query.push({ key: 'sortBy', value: params.sortBy });
            if (params.sortOrder)
                query.push({ key: 'sortOrder', value: params.sortOrder });
        }
        const endpoint = createAbsoluteURL([this.registry.serverUrl, 'admin', 'scans', 'files'], query);
        return sendRequest({
            signal,
            credentials: true,
            endpoint
        });
    }
    async getFileCounts(signal, params) {
        const query = [];
        if (params) {
            if (params.dateDecidedFrom)
                query.push({ key: 'dateDecidedFrom', value: params.dateDecidedFrom });
            if (params.dateDecidedTo)
                query.push({ key: 'dateDecidedTo', value: params.dateDecidedTo });
        }
        const endpoint = createAbsoluteURL([this.registry.serverUrl, 'admin', 'scans', 'files', 'counts'], query);
        return sendRequest({
            signal,
            credentials: true,
            endpoint
        });
    }
    async makeScanDecision(request) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'scans', 'decisions']),
            headers,
            payload: request
        });
    }
    async makeFileDecision(request) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'scans', 'files', 'decisions']),
            headers,
            payload: request
        });
    }
    async deleteFileDecisions(request) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'DELETE',
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'scans', 'files', 'decisions']),
            headers,
            payload: request
        });
    }
    async getSizeOverrides(signal) {
        return sendRequest({
            signal,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'size-overrides']),
            credentials: true
        });
    }
    async createSizeOverride(override) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'POST',
            payload: override,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'size-overrides', 'create']),
            headers
        });
    }
    async updateSizeOverride(id, override) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'PUT',
            payload: override,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'size-overrides', String(id)]),
            headers
        });
    }
    async deleteSizeOverride(id) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'DELETE',
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'size-overrides', String(id)]),
            headers
        });
    }
    async getLogs(signal, page = 0, size = 20, period) {
        const query = [
            { key: 'page', value: page },
            { key: 'size', value: size }
        ];
        if (period) {
            query.push({ key: 'period', value: period });
        }
        const endpoint = addQuery(createAbsoluteURL([this.registry.serverUrl, 'admin', 'logs']), query);
        return sendRequest({
            signal,
            endpoint,
            credentials: true
        });
    }
    async getSettings(signal) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'settings'])
        });
    }
    async updateSettings(settings) {
        const csrfToken = await this.registry.getCsrfToken();
        const headers = {
            'Content-Type': 'application/json;charset=UTF-8'
        };
        headers[csrfToken.header] = csrfToken.value;
        return sendRequest({
            method: 'PUT',
            payload: settings,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'settings']),
            headers
        });
    }
    async getAdminStatistics(signal, year, month) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'statistics'], [
                { key: 'year', value: year },
                { key: 'month', value: month }
            ])
        });
    }
    /**
     * The CSV export is a URL rather than a fetch: the download is a plain link, so the browser
     * saves the file under the name the server's Content-Disposition gives it instead of the page
     * having to build a blob.
     */
    getAdminStatisticsCsvUrl(year, month) {
        return createAbsoluteURL([this.registry.serverUrl, 'admin', 'statistics', 'csv'], [
            { key: 'year', value: year },
            { key: 'month', value: month }
        ]);
    }
    async getSearchIndex(signal) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'search-index'])
        });
    }
    async explainSearch(signal, query, size, offset) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'search-explain'], [
                { key: 'query', value: query },
                { key: 'size', value: size },
                { key: 'offset', value: offset }
            ])
        });
    }
    async updateSearchIndex() {
        const headers = await this.csrfHeaders();
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'update-search-index']),
            headers
        });
    }
    async getCaches(signal) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'caches'])
        });
    }
    async clearCaches(cache) {
        const headers = await this.csrfHeaders();
        // Manager and name travel together or not at all: a cache is only identified by both, and
        // the server clears everything when neither is given.
        const query = cache
            ? [
                { key: 'manager', value: cache.manager },
                { key: 'cache', value: cache.name }
            ]
            : [];
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'caches', 'clear'], query),
            headers
        });
    }
    async getAnalyticsStatus(signal) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'analytics', 'status'])
        });
    }
    async getConsistencyChecks(signal) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'consistency'])
        });
    }
    async getConsistencyFindings(signal, checkId) {
        return sendRequest({
            signal,
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'consistency', checkId, 'findings'])
        });
    }
    async fixConsistencyFindings(checkId) {
        const headers = await this.csrfHeaders();
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([this.registry.serverUrl, 'admin', 'consistency', checkId, 'fix']),
            headers
        });
    }
    async fixConsistencyFinding(checkId, entityId) {
        const headers = await this.csrfHeaders();
        return sendRequest({
            method: 'POST',
            credentials: true,
            endpoint: createAbsoluteURL([
                this.registry.serverUrl,
                'admin',
                'consistency',
                checkId,
                'fix',
                String(entityId)
            ]),
            headers
        });
    }
    async csrfHeaders() {
        const csrfToken = await this.registry.getCsrfToken();
        return { [csrfToken.header]: csrfToken.value };
    }
}
//# sourceMappingURL=extension-registry-service.js.map